import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["General", "Training", "Documentation", "Policy", "Urgent", "Leave"];

// Admin/HR reach everyone or any one employee; a Reporting Officer reaches their
// own team or one member of it. The backend enforces this independently in
// notification_rules.authorize_send — this only shapes what the form offers, so
// the two staying in step is a convenience, not the security boundary.
const AUDIENCES = {
  admin: [
    { value: "all", label: "Everyone", hint: "Every employee who has a portal login" },
    { value: "individual", label: "One specific employee", hint: "Pick the person below" },
  ],
  team: [
    { value: "team", label: "My whole team", hint: "Everyone who reports to you" },
    { value: "individual", label: "One team member", hint: "Pick the person below" },
  ],
};

const form0 = { audience: "", employee_id: "", title: "", message: "", category: "General" };

export default function NotificationComposer({ mode = "admin", open, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(form0);
  const picking = form.audience === "individual";
  const options = AUDIENCES[mode] || AUDIENCES.admin;

  // Only load the people list once it is actually needed — an "everyone" send
  // never has to pull the full employee list down to the phone.
  const { data: people, isLoading: loadingPeople } = useQuery({
    queryKey: ["notifyRecipients", mode],
    enabled: Boolean(open && picking),
    queryFn: async () =>
      mode === "admin"
        ? (await api.get("/employees")).data
        : (await api.get("/my/team")).data.team,
  });

  const close = (v) => {
    if (!v) setForm(form0);
    onOpenChange(v);
  };

  const send = useMutation({
    mutationFn: async () =>
      (await api.post("/notifications", { ...form, employee_id: picking ? form.employee_id : "" })).data,
    onSuccess: (d) => {
      toast.success(`Sent to ${d.sent} ${d.sent === 1 ? "person" : "people"}`);
      qc.invalidateQueries({ queryKey: ["sentNotifications"] });
      close(false);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const ready =
    form.audience &&
    form.title.trim().length >= 3 &&
    form.message.trim().length >= 3 &&
    (!picking || form.employee_id);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white" data-testid="notification-composer">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-navy">Send a Notification</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Send to *</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v, employee_id: "" })}>
              <SelectTrigger className="mt-1" data-testid="notification-audience">
                <SelectValue placeholder="Choose who receives this" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.audience && (
              <p className="text-xs text-slate-400 mt-1">
                {options.find((o) => o.value === form.audience)?.hint}
              </p>
            )}
          </div>

          {picking && (
            <div>
              <Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="mt-1" data-testid="notification-employee">
                  <SelectValue placeholder={loadingPeople ? "Loading…" : "Select employee"} />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {(people || []).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.identity?.name}{e.employee_id ? ` (${e.employee_id})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!loadingPeople && (people || []).length === 0 && (
                <p className="text-xs text-slate-400 mt-1">No employees available to notify.</p>
              )}
            </div>
          )}

          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Title *</Label>
            <Input value={form.title} maxLength={120} className="mt-1"
              placeholder="e.g. Fire safety training due this month"
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="notification-title" />
          </div>

          <div>
            <Label>Message *</Label>
            <Textarea value={form.message} maxLength={2000} rows={5} className="mt-1"
              placeholder="What do they need to know or do, and by when?"
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="notification-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button className="bg-navy hover:bg-navy-700" disabled={!ready || send.isPending}
            onClick={() => send.mutate()} data-testid="notification-send">
            {send.isPending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
